import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import * as XLSX from 'xlsx';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
} from '../../theme/harmonateStyles';

const UploadArea = styled(Box)(() => ({
  border: `2px dashed ${ht.line}`,
  borderRadius: ht.cardRadius,
  padding: '24px 16px',
  textAlign: 'center',
  backgroundColor: ht.listBg,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: ht.blue,
    backgroundColor: ht.secondaryTab,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const normalizeHeader = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const headerMatchNames = (header) => [header.display, ...(header.aliases || [])];

const resolveHeaderIndex = (keyMap, header) => {
  for (const name of headerMatchNames(header)) {
    const idx = keyMap[normalizeHeader(name)];
    if (idx !== undefined) return idx;
  }
  return undefined;
};

const buildHeaderKeyMap = (headerRow) => {
  const keyMap = {};
  headerRow.forEach((cell, index) => {
    const norm = normalizeHeader(cell);
    if (norm) keyMap[norm] = index;
  });
  return keyMap;
};

const cellText = (row, columnIndexByKey, key) => {
  const idx = columnIndexByKey[key];
  if (idx === undefined) return '';
  const value = row[idx];
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/**
 * Reusable Excel/CSV import dialog for tenant-level master screens (no property/business unit).
 *
 * @param {object[]} mandatoryHeaders - [{ display, key, aliases? }]
 * @param {object[]} optionalHeaders - same shape
 * @param {(ctx: { dataRows, columnIndexByKey }) => { items: object[], errors: string[] }} buildItems
 * @param {(items: object[]) => Promise<{ successCount: number, failureCount: number, failed?: { rowLabel?: string, error: string }[] }>} onImport
 */
const MasterImportDialog = ({
  open,
  onClose,
  title,
  scopeNote,
  mandatoryHeaders = [],
  optionalHeaders = [],
  requiredSummary,
  optionalSummary,
  dropHint,
  buildItems,
  onImport,
  onImportSuccess,
  showSuccess,
  showError,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dataRows, setDataRows] = useState([]);
  const [columnIndexByKey, setColumnIndexByKey] = useState(null);
  const [headerError, setHeaderError] = useState('');
  const [parseError, setParseError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const resetState = () => {
    setSelectedFile(null);
    setDataRows([]);
    setColumnIndexByKey(null);
    setHeaderError('');
    setParseError('');
    setIsDragging(false);
    setLastResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (submitting) return;
    resetState();
    onClose();
  };

  const validateHeaders = (headerRow) => {
    const keyMap = buildHeaderKeyMap(headerRow);
    const columnIndex = {};
    const missing = [];
    mandatoryHeaders.forEach((header) => {
      const idx = resolveHeaderIndex(keyMap, header);
      if (idx === undefined) {
        missing.push(header.display);
      } else {
        columnIndex[header.key] = idx;
      }
    });
    if (missing.length) {
      return { missing, columnIndexByKey: null };
    }
    optionalHeaders.forEach((header) => {
      const idx = resolveHeaderIndex(keyMap, header);
      if (idx !== undefined) {
        columnIndex[header.key] = idx;
      }
    });
    return { missing: [], columnIndexByKey: columnIndex };
  };

  const processFile = (file) => {
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv')) {
      setParseError('Unsupported file type. Use .xlsx, .xls, or .csv');
      setSelectedFile(null);
      setDataRows([]);
      setColumnIndexByKey(null);
      return;
    }

    setSelectedFile(file);
    setHeaderError('');
    setParseError('');
    setLastResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
        if (!rows.length) {
          setParseError('File is empty');
          setDataRows([]);
          setColumnIndexByKey(null);
          return;
        }
        const headerRow = rows[0] || [];
        const { missing, columnIndexByKey: indexes } = validateHeaders(headerRow);
        if (missing.length) {
          setHeaderError(`Missing required column header(s): ${missing.join(', ')}. Names must match exactly.`);
          setDataRows([]);
          setColumnIndexByKey(null);
          return;
        }
        const body = rows.slice(1).filter((row) =>
          Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== '')
        );
        if (!body.length) {
          setParseError('No data rows found under the header row');
          setDataRows([]);
          setColumnIndexByKey(null);
          return;
        }
        setColumnIndexByKey(indexes);
        setDataRows(body);
      } catch (err) {
        setParseError(err.message || 'Failed to read file');
        setDataRows([]);
        setColumnIndexByKey(null);
      }
    };
    reader.onerror = () => {
      setParseError('Failed to read file');
      setDataRows([]);
      setColumnIndexByKey(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBrowseClick = () => {
    if (!submitting) fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!submitting) setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (submitting) return;
    processFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    if (!columnIndexByKey || !dataRows.length) {
      (showError || (() => {}))('Choose a valid file with data rows first');
      return;
    }

    const built = buildItems({ dataRows, columnIndexByKey, cellText });
    const items = built?.items || [];
    const buildErrors = built?.errors || [];

    if (buildErrors.length) {
      const message = `Import blocked — fix the following and try again.\n${buildErrors.join('\n')}`;
      setParseError(message);
      (showError || (() => {}))(message);
      return;
    }

    if (!items.length) {
      (showError || (() => {}))('No valid rows to import.');
      return;
    }

    try {
      setSubmitting(true);
      setLastResult(null);
      const result = await onImport(items);
      setLastResult(result);
      const okCount = result?.successCount ?? 0;
      const failCount = result?.failureCount ?? 0;

      if (okCount > 0 && onImportSuccess) {
        onImportSuccess();
      }

      if (failCount === 0) {
        (showSuccess || (() => {}))(`Imported ${okCount} record(s) successfully`);
        resetState();
        onClose();
      } else {
        (showError || (() => {}))(
          `Imported with ${okCount} succeeded and ${failCount} failed — see details below.`
        );
      }
    } catch (err) {
      (showError || (() => {}))(err.message || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const canImport = Boolean(columnIndexByKey && dataRows.length > 0 && !headerError && !parseError);
  const mandatoryLabel = requiredSummary || mandatoryHeaders.map((h) => h.display).join(', ');
  const optionalLabel =
    optionalSummary ||
    (optionalHeaders.length ? optionalHeaders.map((h) => h.display).join(', ') : 'None');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        ...harmonateDialogPaperProps,
        sx: { ...harmonateDialogPaperProps.sx, maxHeight: '95vh' },
      }}
    >
      <DialogTitle sx={harmonateDialogTitleSx}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, color: ht.white }}>
          {title}
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small" aria-label="close" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ ...harmonateDialogContentSx, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {scopeNote && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: ht.listBg,
              border: `1px solid ${ht.line}`,
              borderRadius: ht.fieldRadius,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {scopeNote}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: ht.listBg,
            border: `1px solid ${ht.line}`,
            borderRadius: ht.fieldRadius,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Column headers (first row)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Names must match exactly.
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Required:</strong> {mandatoryLabel}
          </Typography>
          <Typography variant="body2">
            <strong>Optional:</strong> {optionalLabel}
          </Typography>
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Upload file
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {dropHint ||
            'Drag and drop an Excel or CSV file here, or click to browse.'}
        </Typography>

        <UploadArea
          onClick={handleBrowseClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            borderColor: isDragging ? ht.green : ht.line,
            backgroundColor: isDragging ? ht.secondaryTab : ht.listBg,
            pointerEvents: submitting ? 'none' : 'auto',
            opacity: submitting ? 0.6 : 1,
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? ht.green : ht.blue, mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Drop file here or click to browse
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supports .xlsx, .xls, .csv
          </Typography>
        </UploadArea>
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
        />

        {selectedFile && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: ht.listBg,
              border: `1px solid ${ht.line}`,
              borderRadius: ht.fieldRadius,
            }}
          >
            <InsertDriveFileIcon color="action" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dataRows.length} data row{dataRows.length === 1 ? '' : 's'} ready
              </Typography>
            </Box>
          </Paper>
        )}

        {(headerError || parseError) && (
          <Typography variant="body2" color="error" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
            {headerError || parseError}
          </Typography>
        )}

        {lastResult?.failed?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Failed rows
            </Typography>
            <List dense sx={{ maxHeight: 160, overflow: 'auto', bgcolor: 'rgba(239, 63, 63, 0.06)', borderRadius: ht.fieldRadius }}>
              {lastResult.failed.map((item, idx) => (
                <ListItem key={`${item.rowLabel || 'row'}-${idx}`} sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={item.rowLabel || `Row ${idx + 1}`}
                    secondary={item.error}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canImport || submitting}
          sx={harmonateProceedButtonSx}
        >
          {submitting ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { cellText, MasterImportDialog };
export default MasterImportDialog;
